import { Clock, X } from 'lucide-react-native'
import { useState } from 'react'
import { Pressable, StyleSheet, View } from 'react-native'
import { Button, Dialog, Portal, Text, useTheme } from 'react-native-paper'
import DateTimePicker from '@/components/DateTimePicker'
import { formatDateShort, formatTimeOfDayLocale } from '@/lib/dates'
import { useMonitorMoment } from '@/stores/monitor-store'

function formatMoment(at: Date): string {
    const iso = at.toISOString()
    const time = formatTimeOfDayLocale(iso)
    if (at.toDateString() === new Date().toDateString()) return time
    return `${time} · ${formatDateShort(iso)}`
}

export function MonitorMomentPicker() {
    const theme = useTheme()
    const { selectedMoment, setSelectedMoment } = useMonitorMoment()
    const [dialogOpen, setDialogOpen] = useState(false)
    const [draft, setDraftRaw] = useState<Date>(() => selectedMoment ?? new Date())

    // Always clamp drafts to <= now. iOS honors maximumDate on the picker but
    // Android's time-picker can still produce a future timestamp, so we clamp
    // here as belt-and-suspenders.
    const setDraft = (d: Date | null) => {
        if (!d) return
        const now = new Date()
        setDraftRaw(d > now ? now : d)
    }

    const openDialog = () => {
        setDraftRaw(selectedMoment ?? new Date())
        setDialogOpen(true)
    }

    const apply = () => {
        setSelectedMoment(draft)
        setDialogOpen(false)
    }

    const goLive = () => {
        setSelectedMoment(null)
        setDialogOpen(false)
    }

    return (
        <View style={styles.row}>
            {selectedMoment ? (
                <View style={[styles.pill, { backgroundColor: theme.colors.tertiaryContainer }]}>
                    <Clock size={14} color={theme.colors.onTertiaryContainer} />
                    <Pressable onPress={openDialog}>
                        <Text
                            variant="labelMedium"
                            style={{ color: theme.colors.onTertiaryContainer }}
                        >
                            Viewing {formatMoment(selectedMoment)}
                        </Text>
                    </Pressable>
                    <Pressable
                        onPress={goLive}
                        accessibilityLabel="Return to live"
                        accessibilityRole="button"
                        hitSlop={6}
                    >
                        <X size={14} color={theme.colors.onTertiaryContainer} />
                    </Pressable>
                </View>
            ) : (
                <Pressable
                    onPress={openDialog}
                    accessibilityLabel="Pick a point in time"
                    accessibilityRole="button"
                    style={[styles.iconButton, { backgroundColor: theme.colors.surfaceVariant }]}
                >
                    <Clock size={16} color={theme.colors.onSurfaceVariant} />
                </Pressable>
            )}
            <Portal>
                <Dialog
                    visible={dialogOpen}
                    onDismiss={() => setDialogOpen(false)}
                    style={styles.dialog}
                >
                    <Dialog.Title>Pick a point in time</Dialog.Title>
                    <Dialog.Content>
                        <DateTimePicker
                            value={draft}
                            onChange={setDraft}
                            mode="datetime"
                            maximumDate={new Date()}
                        />
                        <Text
                            variant="bodySmall"
                            style={[styles.disclaimer, { color: theme.colors.onSurfaceVariant }]}
                        >
                            On-site detection uses current geofences. If a job's circle has moved
                            since the selected time, on-site status may not match what was actually
                            true then.
                        </Text>
                    </Dialog.Content>
                    <Dialog.Actions>
                        <Button onPress={goLive}>Go to Live</Button>
                        <Button onPress={() => setDialogOpen(false)}>Cancel</Button>
                        <Button mode="contained" onPress={apply}>
                            Apply
                        </Button>
                    </Dialog.Actions>
                </Dialog>
            </Portal>
        </View>
    )
}

const styles = StyleSheet.create({
    row: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    pill: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 16,
    },
    iconButton: {
        padding: 8,
        borderRadius: 16,
    },
    dialog: {
        maxWidth: 500,
        alignSelf: 'center',
        width: '100%',
    },
    disclaimer: {
        marginTop: 12,
    },
})
